import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';

export interface IParkDatePrice {
  date: string;   // YYYY-MM-DD
  price: number;
}

interface ICalendarDay {
  date: string;
  number: number;
  isCurrentMonth: boolean;
  isAvailable: boolean;
  price: number;
  inRange: boolean;    // el ticket cubre este día (desde la fecha de inicio durante los días del ticket)
  isStart: boolean;    // fecha de inicio (la elegida o la que está en preview)
}

const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

/**
 * Calendario de fecha de inicio del ticket de Parques. Se puede elegir cualquier día del rango buscado
 * (llegada → salida): el precio es el mismo en todos y solo cambia según los días del ticket, así que cada día
 * disponible muestra el precio de la opción elegida (en la moneda de Octopus y convertido a la de la agencia).
 * Si se pasa datePrices (tickets add-on), solo esas fechas son elegibles y cada una muestra su propio precio.
 * Con days > 0 se pintan los días que cubre el ticket a partir de la fecha elegida o, mientras se pasa el mouse por
 * una fecha elegible, los que cubriría si se eligiera esa.
 */
@Component({
  selector: 'usb-park-calendar',
  templateUrl: './park-calendar.component.html',
  styleUrls: ['./park-calendar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ParkCalendarComponent implements OnChanges {
  @Input() from: string | Date;           // primer día elegible (llegada)
  @Input() to: string | Date;             // último día elegible (salida)
  @Input() price = 0;                     // precio del ticket elegido (moneda de sourceCurrency)
  @Input() datePrices: IParkDatePrice[] | null = null; // fechas con disponibilidad y su precio (sustituye a from/to/price)
  @Input() selectedDate: string | null = null;
  @Input() days = 0;                      // días que cubre el ticket desde la fecha de inicio
  @Input() sourceCurrency = 'USD';
  @Input() currencies: any[];
  @Input() currencyAgency: any;
  @Input() buttonColorActive = '#6ff3b0';

  @Output() dateChange = new EventEmitter<string>();

  readonly weekdays = ['Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab', 'Dom'];

  year = new Date().getFullYear();
  month = new Date().getMonth();
  calendarDays: ICalendarDay[] = [];

  private hoverDate: string | null = null;

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['from'] || changes['to'] || changes['datePrices']) {
      // Abre en el mes de la fecha elegida o, si no hay, en el de la primera disponible / la llegada.
      const anchor = this.selectedDate || this.datePrices?.[0]?.date || this.iso(this.from);
      if (anchor) {
        const [y, m] = anchor.split('-').map(Number);
        this.year = y;
        this.month = m - 1;
      }
    }
    this.build();
  }

  get monthLabel(): string {
    return `${MONTHS[this.month]} ${this.year}`;
  }

  prevMonth(): void {
    this.shift(-1);
  }

  nextMonth(): void {
    this.shift(1);
  }

  pick(day: ICalendarDay): void {
    if (!day.isCurrentMonth || !day.isAvailable) {
      return;
    }
    this.selectedDate = day.date;
    this.build();
    this.dateChange.emit(day.date);
  }

  preview(day: ICalendarDay | null): void {
    const date = day && day.isAvailable ? day.date : null;
    if (date === this.hoverDate) {
      return;
    }
    this.hoverDate = date;
    this.build();
  }

  private shift(delta: number): void {
    const d = new Date(this.year, this.month + delta, 1);
    this.year = d.getFullYear();
    this.month = d.getMonth();
    this.build();
    this.cdr.markForCheck();
  }

  private build(): void {
    const from = this.iso(this.from);
    const to = this.iso(this.to) || from;
    const prices = this.datePrices ? new Map(this.datePrices.map(d => [d.date, d.price] as [string, number])) : null;
    const startable = (key: string) => (prices ? prices.has(key) : !!from && key >= from && key <= to);

    // Días cubiertos por el ticket: desde la fecha en preview (o la elegida) y tantos como días tenga el ticket.
    const rangeStart = [this.hoverDate, this.selectedDate].find(d => !!d && startable(d)) || '';
    const rangeEnd = rangeStart && Number(this.days) > 0 ? this.addDays(rangeStart, Number(this.days) - 1) : '';

    const first = new Date(this.year, this.month, 1);
    const offset = (first.getDay() + 6) % 7; // lunes = 0
    const inMonth = new Date(this.year, this.month + 1, 0).getDate();
    const total = Math.ceil((offset + inMonth) / 7) * 7;

    this.calendarDays = Array.from({ length: total }, (_, i) => {
      const date = new Date(this.year, this.month, 1 - offset + i);
      const key = this.format(date);
      const current = date.getMonth() === this.month;
      return {
        date: key,
        number: date.getDate(),
        isCurrentMonth: current,
        isAvailable: current && startable(key),
        price: prices ? prices.get(key) ?? 0 : this.price,
        inRange: current && !!rangeEnd && key >= rangeStart && key <= rangeEnd,
        isStart: current && key === rangeStart,
      };
    });
  }

  private addDays(key: string, delta: number): string {
    const [y, m, d] = key.split('-').map(Number);
    return this.format(new Date(y, m - 1, d + delta));
  }

  private iso(value: string | Date): string {
    if (!value) {
      return '';
    }
    return value instanceof Date ? this.format(value) : String(value).substring(0, 10);
  }

  private format(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  trackByDate(_: number, day: ICalendarDay): string {
    return day.date;
  }
}
